import { useState, useEffect, createContext, useContext } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Link, useNavigate, useParams, useLocation, useSearchParams } from "react-router-dom";
import axios from "axios";
import { Toaster, toast } from "sonner";
import { Menu, X, Phone, Mail, MapPin, ShoppingCart, User, LogOut, Palette, Image, Edit, Trash2, Plus, Package, Home as HomeIcon, Star, Lock, Upload, Download, Send, CreditCard, CheckCircle, XCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Auth Context
const AuthContext = createContext(null);

const useAuth = () => useContext(AuthContext);

const AuthProvider = ({ children }) => {
  const [token, setToken] = useState(localStorage.getItem('authToken'));
  const [isAdmin, setIsAdmin] = useState(false);
  const [user, setUser] = useState(null);
  const [isUser, setIsUser] = useState(false);

  useEffect(() => {
    if (token) {
      verifyToken();
    }
  }, [token]);

  const verifyToken = async () => {
    try {
      const response = await axios.get(`${API}/users/profile`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.data.is_admin) {
        setIsAdmin(true);
        setIsUser(false);
        setUser(null);
      } else {
        setUser(response.data);
        setIsUser(true);
        setIsAdmin(false);
      }
    } catch {
      // Try admin verify
      try {
        await axios.get(`${API}/auth/verify`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setIsAdmin(true);
      } catch {
        logout();
      }
    }
  };

  const loginAdmin = (newToken) => {
    localStorage.setItem('authToken', newToken);
    setToken(newToken);
    setIsAdmin(true);
    setIsUser(false);
    setUser(null);
  };

  const loginUser = (newToken, userData) => {
    localStorage.setItem('authToken', newToken);
    setToken(newToken);
    setUser(userData);
    setIsUser(true);
    setIsAdmin(false);
  };

  const logout = () => {
    localStorage.removeItem('authToken');
    setToken(null);
    setIsAdmin(false);
    setIsUser(false);
    setUser(null);
  };

  const updateUser = (userData) => {
    setUser(userData);
  };

  return (
    <AuthContext.Provider value={{ token, isAdmin, isUser, user, loginAdmin, loginUser, logout, updateUser }}>
      {children}
    </AuthContext.Provider>
  );
};

// Navigation Component
const Navigation = () => {
  const [isOpen, setIsOpen] = useState(false);
  const { isAdmin, isUser, user, logout } = useAuth();
  const location = useLocation();

  const navLinks = [
    { href: "/", label: "Home" },
    { href: "/gallery", label: "Gallery" },
    { href: "/custom-order", label: "Custom Order" },
    { href: "/contact", label: "Contact" },
  ];

  const isActive = (path) => location.pathname === path;

  return (
    <nav className="fixed top-0 left-0 right-0 bg-white/95 backdrop-blur-sm z-50 border-b border-gray-100">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="flex items-center justify-between h-20">
          <Link to="/" className="font-heading text-2xl font-semibold tracking-tight" data-testid="logo">
            Sonia Bajwa Art
          </Link>

          {/* Desktop Nav */}
          <div className="hidden md:flex items-center gap-8">
            {navLinks.map((link) => (
              <Link
                key={link.href}
                to={link.href}
                className={`nav-link font-body text-sm uppercase tracking-wide ${isActive(link.href) ? 'text-black' : 'text-gray-600 hover:text-black'}`}
                data-testid={`nav-${link.label.toLowerCase().replace(' ', '-')}`}
              >
                {link.label}
              </Link>
            ))}
            {isAdmin ? (
              <div className="flex items-center gap-4">
                <Link to="/admin" className="nav-link font-body text-sm uppercase tracking-wide text-gray-600 hover:text-black" data-testid="nav-admin">
                  Admin
                </Link>
                <Button variant="outline" size="sm" onClick={logout} data-testid="logout-btn">
                  <LogOut size={16} className="mr-2" /> Logout
                </Button>
              </div>
            ) : isUser ? (
              <div className="flex items-center gap-4">
                <Link to="/profile" className="nav-link font-body text-sm uppercase tracking-wide text-gray-600 hover:text-black" data-testid="nav-profile">
                  {user?.name || 'Profile'}
                </Link>
                <Button variant="outline" size="sm" onClick={logout} data-testid="logout-btn">
                  <LogOut size={16} className="mr-2" /> Logout
                </Button>
              </div>
            ) : (
              <div className="flex items-center gap-4">
                <Link to="/login" className="nav-link font-body text-sm uppercase tracking-wide text-gray-600 hover:text-black" data-testid="nav-login">
                  Login
                </Link>
                <Link to="/register" data-testid="nav-register">
                  <Button size="sm" className="font-body">Register</Button>
                </Link>
              </div>
            )}
          </div>

          {/* Mobile menu button */}
          <button className="md:hidden p-2" onClick={() => setIsOpen(!isOpen)} data-testid="mobile-menu-btn">
            {isOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>

        {/* Mobile Nav */}
        {isOpen && (
          <div className="md:hidden py-4 border-t border-gray-100 animate-fade-in">
            {navLinks.map((link) => (
              <Link
                key={link.href}
                to={link.href}
                className="block py-3 font-body text-base"
                onClick={() => setIsOpen(false)}
                data-testid={`mobile-nav-${link.label.toLowerCase().replace(' ', '-')}`}
              >
                {link.label}
              </Link>
            ))}
            {isAdmin ? (
              <>
                <Link to="/admin" className="block py-3 font-body text-base" onClick={() => setIsOpen(false)}>
                  Admin Dashboard
                </Link>
                <button onClick={() => { logout(); setIsOpen(false); }} className="block py-3 font-body text-base text-red-600">
                  Logout
                </button>
              </>
            ) : isUser ? (
              <>
                <Link to="/profile" className="block py-3 font-body text-base" onClick={() => setIsOpen(false)}>
                  My Profile
                </Link>
                <button onClick={() => { logout(); setIsOpen(false); }} className="block py-3 font-body text-base text-red-600">
                  Logout
                </button>
              </>
            ) : (
              <>
                <Link to="/login" className="block py-3 font-body text-base" onClick={() => setIsOpen(false)}>
                  Login
                </Link>
                <Link to="/register" className="block py-3 font-body text-base font-semibold" onClick={() => setIsOpen(false)}>
                  Register
                </Link>
              </>
            )}
          </div>
        )}
      </div>
    </nav>
  );
};

// Star Rating Component
const StarRating = ({ rating, onRate, readonly = false, size = 20 }) => {
  const [hovered, setHovered] = useState(0);
  
  return (
    <div className="flex gap-1" data-testid="star-rating">
      {[1, 2, 3, 4, 5].map((star) => (
        <button
          key={star}
          type="button"
          disabled={readonly}
          className={`${readonly ? 'cursor-default' : 'cursor-pointer hover:scale-110'} transition-transform`}
          onClick={() => !readonly && onRate && onRate(star)}
          onMouseEnter={() => !readonly && setHovered(star)}
          onMouseLeave={() => !readonly && setHovered(0)}
          data-testid={`star-${star}`}
        >
          <Star
            size={size}
            className={`${
              (hovered || rating) >= star
                ? 'fill-yellow-400 text-yellow-400'
                : 'text-gray-300'
            } transition-colors`}
          />
        </button>
      ))}
    </div>
  );
};

// Newsletter Component
const Newsletter = () => {
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [loading, setLoading] = useState(false);
  const [subscribed, setSubscribed] = useState(false);

  const handleSubscribe = async (e) => {
    e.preventDefault();
    if (!email) {
      toast.error("Please enter your email");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/newsletter/subscribe`, { email, name: name || undefined });
      setSubscribed(true);
      toast.success("Successfully subscribed to the newsletter!");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to subscribe");
    } finally {
      setLoading(false);
    }
  };

  if (subscribed) {
    return (
      <div className="text-center py-4">
        <p className="font-body text-white">Thank you for subscribing! You'll be the first to know about new releases.</p>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubscribe} className="flex flex-col sm:flex-row gap-3 max-w-md mx-auto" data-testid="newsletter-form">
      <Input
        type="email"
        placeholder="Enter your email"
        value={email}
        onChange={(e) => setEmail(e.target.value)}
        className="bg-white/10 border-white/20 text-white placeholder:text-gray-400"
        data-testid="newsletter-email"
      />
      <Button 
        type="submit" 
        className="btn-gold whitespace-nowrap"
        disabled={loading}
        data-testid="newsletter-submit"
      >
        {loading ? 'Subscribing...' : 'Subscribe'}
      </Button>
    </form>
  );
};

// Footer Component
const Footer = () => (
  <footer className="bg-black text-white py-16">
    <div className="max-w-7xl mx-auto px-6 md:px-12">
      {/* Newsletter Section */}
      <div className="text-center mb-12 pb-12 border-b border-gray-800">
        <h3 className="font-heading text-2xl mb-3">Stay Updated</h3>
        <p className="font-body text-gray-400 text-sm mb-6 max-w-lg mx-auto">
          Subscribe to get notified about new art releases, exclusive offers, and upcoming exhibitions.
        </p>
        <Newsletter />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-12">
        <div>
          <h3 className="font-heading text-2xl mb-4">Sonia Bajwa Art</h3>
          <p className="font-body text-gray-400 text-sm leading-relaxed">
            Contemporary art for modern homes. Original paintings and bespoke commissions crafted with passion and soul.
          </p>
        </div>
        <div>
          <h4 className="font-body font-semibold uppercase tracking-wide text-sm mb-4">Quick Links</h4>
          <div className="space-y-2">
            <Link to="/gallery" className="block text-gray-400 hover:text-white text-sm" data-testid="footer-gallery">Gallery</Link>
            <Link to="/custom-order" className="block text-gray-400 hover:text-white text-sm" data-testid="footer-custom">Custom Order</Link>
            <Link to="/contact" className="block text-gray-400 hover:text-white text-sm" data-testid="footer-contact">Contact</Link>
          </div>
        </div>
        <div>
          <h4 className="font-body font-semibold uppercase tracking-wide text-sm mb-4">Contact</h4>
          <div className="space-y-3 text-sm text-gray-400">
            <p className="flex items-center gap-2">
              <Mail size={16} /> bajwahomegoods@gmail.com
            </p>
            <p className="flex items-center gap-2">
              <Phone size={16} /> 724-272-8378
            </p>
            <p className="flex items-start gap-2">
              <MapPin size={16} className="mt-1 flex-shrink-0" /> 
              Cranberry Township, PA 16066, U.S.A.
            </p>
          </div>
        </div>
      </div>
      <div className="border-t border-gray-800 mt-12 pt-8 text-center text-gray-500 text-sm">
        © {new Date().getFullYear()} Sonia Bajwa Art. All rights reserved.
      </div>
    </div>
  </footer>
);

// Home Page
const Home = () => {
  const [paintings, setPaintings] = useState([]);
  const navigate = useNavigate();

  useEffect(() => {
    fetchPaintings();
  }, []);

  const fetchPaintings = async () => {
    try {
      const response = await axios.get(`${API}/paintings`);
      setPaintings(response.data.slice(0, 4));
    } catch (error) {
      console.error("Error fetching paintings:", error);
    }
  };

  return (
    <div className="pt-20" data-testid="home-page">
      {/* Hero Section */}
      <section className="relative h-[90vh] flex items-center justify-center overflow-hidden">
        <div className="absolute inset-0 bg-black">
          <img 
            src="https://images.unsplash.com/photo-1590335313253-bf224242b3cb?auto=format&fit=crop&q=80&w=1920"
            alt="Art studio background"
            className="w-full h-full object-cover opacity-60"
          />
        </div>
        <div className="relative z-10 text-center text-white px-6 max-w-4xl mx-auto animate-fade-in">
          <h1 className="font-heading text-5xl md:text-7xl font-semibold mb-6">
            Art That Speaks <br /> to Your Soul
          </h1>
          <p className="font-body text-lg md:text-xl text-gray-200 mb-8 max-w-2xl mx-auto">
            Original paintings and bespoke commissions by Sonia Bajwa. Each piece tells a unique story.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button 
              size="lg" 
              className="btn-gold px-8 py-6 text-base font-body"
              onClick={() => navigate('/gallery')}
              data-testid="hero-view-gallery"
            >
              View Gallery
            </Button>
            <Button 
              variant="outline" 
              size="lg" 
              className="border-white text-white hover:bg-white hover:text-black px-8 py-6 text-base font-body"
              onClick={() => navigate('/custom-order')}
              data-testid="hero-commission"
            >
              Commission a Piece
            </Button>
          </div>
        </div>
      </section>

      {/* Featured Works */}
      <section className="py-24 md:py-32 px-6 md:px-12">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="font-heading text-4xl md:text-5xl mb-4 animate-fade-in">Featured Works</h2>
            <p className="font-body text-gray-600 text-lg animate-fade-in-delay-1">Discover the latest additions to the collection</p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {paintings.map((painting, index) => (
              <div 
                key={painting.id}
                className={`painting-card cursor-pointer animate-fade-in-delay-${index % 3 + 1}`}
                onClick={() => navigate(`/painting/${painting.id}`)}
                data-testid={`featured-painting-${index}`}
              >
                <div className="aspect-[3/4] overflow-hidden bg-gray-100 mb-4">
                  <img 
                    src={painting.image_url} 
                    alt={painting.title}
                    className="w-full h-full object-cover"
                  />
                </div>
                <h3 className="font-heading text-xl mb-1">{painting.title}</h3>
                <p className="font-body text-gray-500 text-sm mb-2">{painting.category}</p>
                <p className="font-accent text-lg font-medium">${painting.price.toFixed(2)}</p>
              </div>
            ))}
          </div>

          <div className="text-center mt-12">
            <Button 
              variant="outline" 
              size="lg" 
              className="font-body"
              onClick={() => navigate('/gallery')}
              data-testid="view-all-works"
            >
              View All Works
            </Button>
          </div>
        </div>
      </section>

      {/* About Section */}
      <section className="py-24 bg-gray-50 px-6 md:px-12">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div className="order-2 lg:order-1">
              <h2 className="font-heading text-4xl md:text-5xl mb-6">Meet the Artist</h2>
              <p className="font-body text-gray-600 text-sm leading-relaxed mb-4">
                Sonia Bajwa is a contemporary artist whose work spans a wide range of mediums, including oil, watercolor, acrylics, and ink art. Her paintings reflect a blend of realism, abstraction, and spiritual themes, often inspired by nature, culture, and human emotion. Each piece is created with attention to detail and a deep sense of expression, resulting in artwork that connects naturally with collectors and art lovers.
              </p>
              <p className="font-body text-gray-600 text-sm leading-relaxed mb-4">
                In addition to her paintings, Sonia explores decorative and mixed-media forms such as decoupage and handcrafted beadwork. These pieces showcase her versatility and creativity, bringing color, texture, and personality to everyday objects and interior spaces.
              </p>
              <p className="font-body text-gray-600 text-sm leading-relaxed mb-6">
                Sonia also offers painting classes for students of all age groups, from beginners to more experienced learners. Her teaching approach is friendly, encouraging, and adaptable, helping each student develop their own style and confidence. Whether creating original artwork, custom commissions, or guiding others through the creative process, her goal is to bring warmth, beauty, and inspiration into every space.
              </p>
              <Button 
                className="font-body"
                onClick={() => navigate('/custom-order')}
                data-testid="about-commission"
              >
                Commission a Custom Piece
              </Button>
            </div>
            <div className="order-1 lg:order-2">
              <div className="aspect-square bg-gray-200 overflow-hidden">
                <img 
                  src="https://images.unsplash.com/photo-1608054361605-1bfd5a2b31c7?auto=format&fit=crop&q=80&w=800"
                  alt="Artist at work"
                  className="w-full h-full object-cover"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Custom Order CTA */}
      <section className="py-24 bg-black text-white px-6 md:px-12">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="font-heading text-4xl md:text-5xl mb-6">Want Something Unique?</h2>
          <p className="font-body text-gray-400 text-lg mb-8 max-w-2xl mx-auto">
            Commission a custom piece tailored to your vision. Choose your medium, size, and describe your dream artwork.
          </p>
          <Button 
            size="lg" 
            className="btn-gold px-10 py-6 text-base font-body"
            onClick={() => navigate('/custom-order')}
            data-testid="cta-custom-order"
          >
            Start Your Custom Order
          </Button>
        </div>
      </section>
    </div>
  );
};

// Gallery Page
const Gallery = () => {
  const [items, setItems] = useState([]);
  const [productType, setProductType] = useState("painting");
  const [category, setCategory] = useState("All");
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  const paintingCategories = ["All", "Oil", "Acrylic", "Watercolor", "Mixed Media", "Other"];
  const jewelryCategories = ["All", "Necklaces", "Earrings", "Bracelets", "Rings", "Other"];

  const categories = productType === "painting" ? paintingCategories : jewelryCategories;

  useEffect(() => {
    setCategory("All"); // Reset category when switching product type
  }, [productType]);

  useEffect(() => {
    fetchItems();
  }, [category, productType]);

  const fetchItems = async () => {
    setLoading(true);
    try {
      let url = `${API}/paintings?product_type=${productType}`;
      if (category !== "All") {
        url += `&category=${category}`;
      }
      const response = await axios.get(url);
      setItems(response.data);
    } catch (error) {
      toast.error("Failed to load items");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="pt-20 min-h-screen" data-testid="gallery-page">
      {/* Header */}
      <section className="py-16 px-6 md:px-12 text-center">
        <h1 className="font-heading text-5xl md:text-6xl mb-4">Gallery</h1>
        <p className="font-body text-gray-600 text-lg max-w-2xl mx-auto">
          Explore the complete collection of original artworks and handmade jewelry
        </p>
      </section>

      {/* Product Type Tabs */}
      <section className="px-6 md:px-12 mb-8">
        <div className="max-w-7xl mx-auto">
          <div className="flex justify-center gap-4 border-b border-gray-200">
            <button
              onClick={() => setProductType("painting")}
              className={`px-6 py-3 font-heading text-lg border-b-2 transition-colors ${
                productType === "painting" 
                  ? "border-black text-black" 
                  : "border-transparent text-gray-500 hover:text-black"
              }`}
              data-testid="tab-paintings"
            >
              Paintings
            </button>
            <button
              onClick={() => setProductType("jewelry")}
              className={`px-6 py-3 font-heading text-lg border-b-2 transition-colors ${
                productType === "jewelry" 
                  ? "border-black text-black" 
                  : "border-transparent text-gray-500 hover:text-black"
              }`}
              data-testid="tab-jewelry"
            >
              Hand Made Jewelry
            </button>
          </div>
        </div>
      </section>

      {/* Category Filter */}
      <section className="px-6 md:px-12 mb-12">
        <div className="max-w-7xl mx-auto">
          <div className="flex flex-wrap justify-center gap-3">
            {categories.map((cat) => (
              <button
                key={cat}
                onClick={() => setCategory(cat)}
                className={`category-pill rounded-sm ${category === cat ? 'active' : ''}`}
                data-testid={`category-${cat.toLowerCase().replace(' ', '-')}`}
              >
                {cat}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Items Grid */}
      <section className="px-6 md:px-12 pb-24">
        <div className="max-w-7xl mx-auto">
          {loading ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {[1, 2, 3, 4, 5, 6].map((i) => (
                <div key={i} className="animate-pulse">
                  <div className="aspect-[3/4] bg-gray-200 mb-4"></div>
                  <div className="h-6 bg-gray-200 w-3/4 mb-2"></div>
                  <div className="h-4 bg-gray-200 w-1/2"></div>
                </div>
              ))}
            </div>
          ) : items.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {items.map((item) => (
                <div 
                  key={item.id}
                  className="painting-card cursor-pointer"
                  onClick={() => navigate(`/painting/${item.id}`)}
                  data-testid={`painting-card-${item.id}`}
                >
                  <div className="aspect-[3/4] overflow-hidden bg-gray-100 mb-4 relative">
                    <img 
                      src={item.image_url} 
                      alt={item.title}
                      className="w-full h-full object-cover"
                    />
                    {!item.is_available && (
                      <div className="absolute bottom-3 right-3">
                        <Badge variant="secondary" className="text-sm font-accent bg-black text-white">SOLD</Badge>
                      </div>
                    )}
                  </div>
                  <div className="flex justify-between items-start">
                    <div>
                      <h3 className="font-heading text-xl mb-1">{item.title}</h3>
                      <p className="font-body text-gray-500 text-sm">{item.category}</p>
                    </div>
                    <p className="font-accent text-lg font-medium">${item.price.toFixed(2)}</p>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-16">
              <p className="font-body text-gray-500 text-lg">
                No {productType === "painting" ? "paintings" : "jewelry"} found in this category.
              </p>
            </div>
          )}
        </div>
      </section>
    </div>
  );
};

// Painting Detail Page
const PaintingDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { isUser, user } = useAuth();
  const [painting, setPainting] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showPurchase, setShowPurchase] = useState(false);
  const [checkoutMode, setCheckoutMode] = useState(null); // 'guest' or 'user'
  const [formData, setFormData] = useState({
    customer_name: '',
    customer_email: '',
    customer_phone: ''
  });
  
  // Reviews state
  const [reviews, setReviews] = useState([]);
  const [reviewStats, setReviewStats] = useState({ average_rating: 0, total_reviews: 0 });
  const [newReview, setNewReview] = useState({
    reviewer_name: '',
    rating: 0,
    comment: ''
  });
  const [submittingReview, setSubmittingReview] = useState(false);

  useEffect(() => {
    fetchPainting();
    fetchReviews();
  }, [id]);

  useEffect(() => {
    // Pre-fill form if user is logged in
    if (isUser && user) {
      setFormData({
        customer_name: user.name || '',
        customer_email: user.email || '',
        customer_phone: user.phone || ''
      });
      setCheckoutMode('user');
    }
  }, [isUser, user]);

  const fetchPainting = async () => {
    try {
      const response = await axios.get(`${API}/paintings/${id}`);
      setPainting(response.data);
    } catch (error) {
      toast.error("Painting not found");
      navigate('/gallery');
    } finally {
      setLoading(false);
    }
  };

  const fetchReviews = async () => {
    try {
      const [reviewsRes, statsRes] = await Promise.all([
        axios.get(`${API}/reviews/${id}`),
        axios.get(`${API}/reviews/${id}/stats`)
      ]);
      setReviews(reviewsRes.data);
      setReviewStats(statsRes.data);
    } catch (error) {
      console.error("Error fetching reviews:", error);
    }
  };

  const handleSubmitReview = async (e) => {
    e.preventDefault();
    if (newReview.rating === 0) {
      toast.error("Please select a star rating");
      return;
    }
    if (!newReview.comment.trim()) {
      toast.error("Please write a comment");
      return;
    }

    setSubmittingReview(true);
    try {
      await axios.post(`${API}/reviews`, {
        painting_id: id,
        reviewer_name: newReview.reviewer_name || "Anonymous",
        rating: newReview.rating,
        comment: newReview.comment
      });
      toast.success("Review submitted successfully!");
      setNewReview({ reviewer_name: '', rating: 0, comment: '' });
      fetchReviews();
    } catch (error) {
      toast.error("Failed to submit review");
    } finally {
      setSubmittingReview(false);
    }
  };

  const [paymentLoading, setPaymentLoading] = useState(false);

  const handleStripeCheckout = async () => {
    if (!formData.customer_name || !formData.customer_email || !formData.customer_phone) {
      toast.error("Please fill in your name, email, and phone number");
      return;
    }

    setPaymentLoading(true);
    try {
      const response = await axios.post(`${API}/payments/checkout`, {
        painting_id: painting.id,
        origin_url: window.location.origin,
        customer_name: formData.customer_name,
        customer_email: formData.customer_email,
        customer_phone: formData.customer_phone,
      });
      // Redirect to Stripe Checkout
      window.location.href = response.data.checkout_url;
    } catch (error) {
      toast.error(error.response?.data?.detail || "Payment failed. Please try again.");
      setPaymentLoading(false);
    }
  };

  const handlePayPalCheckout = () => {
    if (!formData.customer_name || !formData.customer_email || !formData.customer_phone) {
      toast.error("Please fill in your name, email, and phone number");
      return;
    }
    const amount = painting.price.toFixed(2);
    const itemName = encodeURIComponent(painting.title);
    const paypalUrl = `https://www.paypal.com/cgi-bin/webscr?cmd=_xclick&business=7V3B4ZSYYALA8&amount=${amount}&currency_code=USD&item_name=${itemName}&no_shipping=1`;
    window.open(paypalUrl, '_blank');
    toast.success("PayPal payment window opened. Complete payment there.");
    // Save order in DB
    axios.post(`${API}/purchases`, {
      painting_id: painting.id,
      ...formData
    }).catch(() => {});
  };

  if (loading) {
    return (
      <div className="pt-20 min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-black"></div>
      </div>
    );
  }

  if (!painting) return null;

  return (
    <div className="pt-20 min-h-screen" data-testid="painting-detail-page">
      <div className="max-w-7xl mx-auto px-6 md:px-12 py-12">
        <button 
          onClick={() => navigate('/gallery')}
          className="font-body text-gray-600 hover:text-black mb-8 flex items-center gap-2"
          data-testid="back-to-gallery"
        >
          ← Back to Gallery
        </button>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
          {/* Image */}
          <div className="aspect-square bg-gray-100 overflow-hidden">
            <img 
              src={painting.image_url} 
              alt={painting.title}
              className="w-full h-full object-contain"
              data-testid="painting-image"
            />
          </div>

          {/* Details */}
          <div className="flex flex-col justify-center">
            <Badge variant="outline" className="w-fit mb-4 font-accent">{painting.category}</Badge>
            <h1 className="font-heading text-4xl md:text-5xl mb-4" data-testid="painting-title">{painting.title}</h1>
            <p className="font-body text-gray-600 leading-relaxed mb-6" data-testid="painting-description">
              {painting.description}
            </p>
            <p className="font-accent text-3xl font-semibold mb-8" data-testid="painting-price">
              ${painting.price.toFixed(2)}
            </p>

            {painting.is_available ? (
              <Button 
                size="lg" 
                className="btn-gold w-full md:w-auto px-12 py-6 text-base font-body"
                onClick={() => setShowPurchase(true)}
                data-testid="buy-now-btn"
              >
                <ShoppingCart className="mr-2" size={20} />
                Buy Now
              </Button>
            ) : (
              <Button disabled size="lg" className="w-full md:w-auto px-12 py-6 text-base font-body">
                Sold
              </Button>
            )}

            <div className="mt-8 pt-8 border-t border-gray-200">
              <p className="font-body text-sm text-gray-600 mb-3">
                <strong>Recreations Available:</strong> Interested in a sold piece? We can recreate any artwork in your preferred size. Each recreation is hand-painted with the same care and attention as the original.
              </p>
              <p className="font-body text-sm text-gray-600 mb-3">
                <strong>Framing Available:</strong> Professional framing is provided with extra charges depending upon the design of frame. Contact us for framing options and pricing.
              </p>
              <p className="font-body text-sm text-gray-500">
                Have questions about this piece? <Link to="/contact" className="text-black underline">Contact us</Link>
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Purchase Dialog */}
      <Dialog open={showPurchase} onOpenChange={(open) => { setShowPurchase(open); if (!open) setCheckoutMode(isUser ? 'user' : null); }}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="font-heading text-2xl">Complete Your Purchase</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="flex items-center gap-4 p-4 bg-gray-50 rounded-sm">
              <div className="w-20 h-20 bg-gray-200 overflow-hidden">
                <img src={painting.image_url} alt={painting.title} className="w-full h-full object-cover" />
              </div>
              <div>
                <p className="font-heading text-lg">{painting.title}</p>
                <p className="font-accent text-xl font-semibold">${painting.price.toFixed(2)}</p>
              </div>
            </div>

            {/* Checkout Mode Selection */}
            {!isUser && !checkoutMode && (
              <div className="space-y-3">
                <p className="font-body text-sm text-gray-600 text-center">How would you like to checkout?</p>
                <div className="grid grid-cols-2 gap-3">
                  <Button 
                    variant="outline" 
                    className="h-auto py-4 flex flex-col"
                    onClick={() => setCheckoutMode('guest')}
                    data-testid="checkout-guest"
                  >
                    <User size={24} className="mb-2" />
                    <span className="font-body text-sm">Guest Checkout</span>
                  </Button>
                  <Button 
                    className="h-auto py-4 flex flex-col"
                    onClick={() => navigate('/login')}
                    data-testid="checkout-login"
                  >
                    <User size={24} className="mb-2" />
                    <span className="font-body text-sm">Login / Register</span>
                  </Button>
                </div>
                <p className="font-body text-xs text-gray-400 text-center">
                  Register to save your info for faster checkout next time
                </p>
              </div>
            )}

            {/* Checkout Form */}
            {(isUser || checkoutMode === 'guest') && (
              <div className="space-y-4">
                {isUser && (
                  <div className="p-3 bg-green-50 rounded-sm">
                    <p className="font-body text-sm text-green-800">Logged in as {user?.name}</p>
                  </div>
                )}
                <div>
                  <Label className="font-body text-sm">Full Name *</Label>
                  <Input 
                    className="mt-1"
                    value={formData.customer_name}
                    onChange={(e) => setFormData({...formData, customer_name: e.target.value})}
                    placeholder="Your full name"
                    data-testid="purchase-name"
                  />
                </div>
                <div>
                  <Label className="font-body text-sm">Email *</Label>
                  <Input 
                    type="email"
                    className="mt-1"
                    value={formData.customer_email}
                    onChange={(e) => setFormData({...formData, customer_email: e.target.value})}
                    placeholder="your@email.com"
                    data-testid="purchase-email"
                />
              </div>
              <div>
                <Label className="font-body text-sm">Phone *</Label>
                <Input 
                  className="mt-1"
                  value={formData.customer_phone}
                  onChange={(e) => setFormData({...formData, customer_phone: e.target.value})}
                  placeholder="Your phone number"
                  data-testid="purchase-phone"
                />
              </div>
              </div>
            )}
          </div>
          <DialogFooter className="flex-col gap-3 sm:flex-col">
            {(isUser || checkoutMode === 'guest') && (
              <>
                <Button 
                  className="w-full btn-gold py-6" 
                  onClick={handleStripeCheckout} 
                  disabled={paymentLoading}
                  data-testid="stripe-checkout-btn"
                >
                  <CreditCard size={18} className="mr-2" />
                  {paymentLoading ? 'Redirecting...' : `Pay $${painting.price.toFixed(2)} with Card`}
                </Button>
                <Button 
                  variant="outline"
                  className="w-full py-6" 
                  onClick={handlePayPalCheckout}
                  data-testid="paypal-checkout-btn"
                >
                  Pay with PayPal
                </Button>
                <Button variant="ghost" size="sm" onClick={() => setShowPurchase(false)} className="w-full">
                  Cancel
                </Button>
              </>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Reviews Section */}
      <div className="max-w-7xl mx-auto px-6 md:px-12 py-16 border-t border-gray-200">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-12">
          {/* Reviews List */}
          <div className="lg:col-span-2">
            <div className="flex items-center gap-4 mb-8">
              <h2 className="font-heading text-3xl">Reviews</h2>
              {reviewStats.total_reviews > 0 && (
                <div className="flex items-center gap-2">
                  <StarRating rating={Math.round(reviewStats.average_rating)} readonly size={18} />
                  <span className="font-body text-gray-600">
                    {reviewStats.average_rating} ({reviewStats.total_reviews} {reviewStats.total_reviews === 1 ? 'review' : 'reviews'})
                  </span>
                </div>
              )}
            </div>

            {reviews.length === 0 ? (
              <p className="font-body text-gray-500 py-8">No reviews yet. Be the first to share your thoughts!</p>
            ) : (
              <div className="space-y-6" data-testid="reviews-list">
                {reviews.map((review) => (
                  <div key={review.id} className="border-b border-gray-100 pb-6" data-testid={`review-${review.id}`}>
                    <div className="flex items-center justify-between mb-2">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 bg-gray-200 rounded-full flex items-center justify-center font-heading text-lg">
                          {review.reviewer_name.charAt(0).toUpperCase()}
                        </div>
                        <div>
                          <p className="font-body font-medium">{review.reviewer_name}</p>
                          <p className="font-body text-sm text-gray-500">
                            {new Date(review.created_at).toLocaleDateString('en-US', { 
                              year: 'numeric', 
                              month: 'long', 
                              day: 'numeric' 
                            })}
                          </p>
                        </div>
                      </div>
                      <StarRating rating={review.rating} readonly size={16} />
                    </div>
                    <p className="font-body text-gray-700 mt-3 ml-13">{review.comment}</p>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Add Review Form */}
          <div className="lg:col-span-1">
            <Card className="sticky top-28">
              <CardHeader>
                <CardTitle className="font-heading text-xl">Write a Review</CardTitle>
              </CardHeader>
              <CardContent>
                <form onSubmit={handleSubmitReview} className="space-y-4">
                  <div>
                    <Label className="font-body text-sm">Your Name (optional)</Label>
                    <Input
                      className="mt-1"
                      value={newReview.reviewer_name}
                      onChange={(e) => setNewReview({...newReview, reviewer_name: e.target.value})}
                      placeholder="Anonymous"
                      data-testid="review-name"
                    />
                  </div>

                  <div>
                    <Label className="font-body text-sm mb-2 block">Rating *</Label>
                    <StarRating
                      rating={newReview.rating}
                      onRate={(rating) => setNewReview({...newReview, rating})}
                      size={28}
                    />
                  </div>

                  <div>
                    <Label className="font-body text-sm">Your Comment *</Label>
                    <Textarea
                      className="mt-1 min-h-[100px]"
                      value={newReview.comment}
                      onChange={(e) => setNewReview({...newReview, comment: e.target.value})}
                      placeholder="Share your thoughts about this artwork..."
                      data-testid="review-comment"
                    />
                  </div>

                  <Button
                    type="submit"
                    className="w-full font-body"
                    disabled={submittingReview}
                    data-testid="submit-review-btn"
                  >
                    {submittingReview ? 'Submitting...' : 'Submit Review'}
                  </Button>
                </form>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
};

// Custom Order Page
const CustomOrder = () => {
  const { isUser, user } = useAuth();
  const navigate = useNavigate();
  const [checkoutMode, setCheckoutMode] = useState(null);
  const [productType, setProductType] = useState('painting');
  const [formData, setFormData] = useState({
    customer_name: '',
    customer_email: '',
    customer_phone: '',
    product_type: 'painting',
    color_type: '',
    jewelry_type: '',
    size: '',
    description: ''
  });
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    if (isUser && user) {
      setFormData(prev => ({
        ...prev,
        customer_name: user.name || '',
        customer_email: user.email || '',
        customer_phone: user.phone || ''
      }));
      setCheckoutMode('user');
    }
  }, [isUser, user]);

  const sizeOptions = [
    { value: 'small', label: 'Small (4" x 8")', price: '$50' },
    { value: 'medium', label: 'Medium (12" x 12")', price: '$100' },
    { value: 'large', label: 'Large (24" x 36")', price: '$150' }
  ];

  const colorTypes = [
    { value: 'water', label: 'Watercolor' },
    { value: 'oil', label: 'Oil' },
    { value: 'acrylic', label: 'Acrylic' },
    { value: 'ink', label: 'Ink' },
    { value: 'other', label: 'Other' }
  ];

  const jewelryTypes = [
    { value: 'necklace', label: 'Necklace' },
    { value: 'earrings', label: 'Earrings' },
    { value: 'bracelet', label: 'Bracelet' },
    { value: 'ring', label: 'Ring' },
    { value: 'other', label: 'Other' }
  ];

  const handleProductTypeChange = (type) => {
    setProductType(type);
    setFormData({ ...formData, product_type: type, color_type: '', jewelry_type: '' });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    const isPainting = productType === 'painting';
    if (!formData.customer_name || !formData.customer_email || !formData.customer_phone || !formData.size || !formData.description) {
      toast.error("Please fill in all required fields");
      return;
    }
    if (isPainting && !formData.color_type) {
      toast.error("Please select a color type");
      return;
    }
    if (!isPainting && !formData.jewelry_type) {
      toast.error("Please select a jewelry type");
      return;
    }

    setLoading(true);
    try {
      await axios.post(`${API}/orders/custom`, formData);
      setSuccess(true);
      toast.success("Custom order submitted successfully!");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to submit order");
    } finally {
      setLoading(false);
    }
  };

  if (success) {
    return (
      <div className="pt-20 min-h-screen flex items-center justify-center px-6" data-testid="order-success">
        <div className="text-center max-w-lg">
          <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
            <Package className="text-green-600" size={40} />
          </div>
          <h1 className="font-heading text-4xl mb-4">Order Submitted!</h1>
          <p className="font-body text-gray-600 mb-8">
            Thank you for your custom order request. Sonia will review your specifications and get back to you within 2-3 business days.
          </p>
          <Button onClick={() => window.location.href = '/gallery'} className="font-body">
            Continue Browsing
          </Button>
        </div>
      </div>
    );
  }

  // Show checkout mode selection for guests
  if (!isUser && !checkoutMode) {
    return (
      <div className="pt-20 min-h-screen" data-testid="custom-order-page">
        <section className="py-16 px-6 md:px-12 text-center">
          <h1 className="font-heading text-5xl md:text-6xl mb-4">Custom Order</h1>
          <p className="font-body text-gray-600 text-lg max-w-2xl mx-auto">
            Commission a unique piece of art tailored to your vision
          </p>
        </section>
        <section className="px-6 md:px-12 pb-24">
          <div className="max-w-md mx-auto">
            <Card className="border-0 shadow-lg">
              <CardContent className="p-8">
                <p className="font-body text-center mb-6">How would you like to proceed?</p>
                <div className="space-y-4">
                  <Button 
                    className="w-full h-auto py-4 flex flex-col btn-gold"
                    onClick={() => navigate('/register')}
                    data-testid="custom-register"
                  >
                    <User size={24} className="mb-2" />
                    <span className="font-body">Register / Login</span>
                    <span className="font-body text-xs opacity-80">Save your info for future orders</span>
                  </Button>
                  <Button 
                    variant="outline"
                    className="w-full h-auto py-4 flex flex-col"
                    onClick={() => setCheckoutMode('guest')}
                    data-testid="custom-guest"
                  >
                    <User size={24} className="mb-2" />
                    <span className="font-body">Continue as Guest</span>
                  </Button>
                </div>
              </CardContent>
            </Card>
          </div>
        </section>
      </div>
    );
  }

  return (
    <div className="pt-20 min-h-screen" data-testid="custom-order-page">
      <section className="py-16 px-6 md:px-12 text-center">
        <h1 className="font-heading text-5xl md:text-6xl mb-4">Custom Order</h1>
        <p className="font-body text-gray-600 text-lg max-w-2xl mx-auto">
          Commission a unique piece of art tailored to your vision
        </p>
      </section>

      <section className="px-6 md:px-12 pb-24">
        <div className="max-w-2xl mx-auto">
          <Card className="border-0 shadow-lg">
            <CardContent className="p-8">
              <form onSubmit={handleSubmit} className="space-y-6">
                {/* Product Type Selector */}
                <div>
                  <Label className="font-body text-sm mb-2 block">What would you like to order? *</Label>
                  <div className="flex gap-3">
                    <Button
                      type="button"
                      variant={productType === 'painting' ? 'default' : 'outline'}
                      className={`flex-1 h-auto py-3 ${productType === 'painting' ? 'btn-gold' : ''}`}
                      onClick={() => handleProductTypeChange('painting')}
                      data-testid="custom-type-painting"
                    >
                      <Palette size={18} className="mr-2" /> Custom Painting
                    </Button>
                    <Button
                      type="button"
                      variant={productType === 'jewelry' ? 'default' : 'outline'}
                      className={`flex-1 h-auto py-3 ${productType === 'jewelry' ? 'btn-gold' : ''}`}
                      onClick={() => handleProductTypeChange('jewelry')}
                      data-testid="custom-type-jewelry"
                    >
                      Custom Jewelry
                    </Button>
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <Label className="font-body text-sm">Full Name *</Label>
                    <Input 
                      className="mt-1"
                      value={formData.customer_name}
                      onChange={(e) => setFormData({...formData, customer_name: e.target.value})}
                      placeholder="Your full name"
                      data-testid="custom-name"
                    />
                  </div>
                  <div>
                    <Label className="font-body text-sm">Email *</Label>
                    <Input 
                      type="email"
                      className="mt-1"
                      value={formData.customer_email}
                      onChange={(e) => setFormData({...formData, customer_email: e.target.value})}
                      placeholder="your@email.com"
                      data-testid="custom-email"
                    />
                  </div>
                </div>

                <div>
                  <Label className="font-body text-sm">Phone *</Label>
                  <Input 
                    className="mt-1"
                    value={formData.customer_phone}
                    onChange={(e) => setFormData({...formData, customer_phone: e.target.value})}
                    placeholder="Your phone number"
                    data-testid="custom-phone"
                  />
                </div>

                {/* Conditional: Painting Color Type or Jewelry Type */}
                {productType === 'painting' ? (
                  <div>
                    <Label className="font-body text-sm">Color Type *</Label>
                    <Select value={formData.color_type} onValueChange={(value) => setFormData({...formData, color_type: value})}>
                      <SelectTrigger className="mt-1" data-testid="custom-color-type">
                        <SelectValue placeholder="Select a medium" />
                      </SelectTrigger>
                      <SelectContent>
                        {colorTypes.map((type) => (
                          <SelectItem key={type.value} value={type.value}>
                            {type.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                ) : (
                  <div>
                    <Label className="font-body text-sm">Jewelry Type *</Label>
                    <Select value={formData.jewelry_type} onValueChange={(value) => setFormData({...formData, jewelry_type: value})}>
                      <SelectTrigger className="mt-1" data-testid="custom-jewelry-type">
                        <SelectValue placeholder="Select jewelry type" />
                      </SelectTrigger>
                      <SelectContent>
                        {jewelryTypes.map((type) => (
                          <SelectItem key={type.value} value={type.value}>
                            {type.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                )}

                <div>
                  <Label className="font-body text-sm">Size *</Label>
                  <Select value={formData.size} onValueChange={(value) => setFormData({...formData, size: value})}>
                    <SelectTrigger className="mt-1" data-testid="custom-size">
                      <SelectValue placeholder="Select a size" />
                    </SelectTrigger>
                    <SelectContent>
                      {sizeOptions.map((option) => (
                        <SelectItem key={option.value} value={option.value}>
                          {option.label} - {option.price}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <Label className="font-body text-sm">
                    {productType === 'painting' ? 'Description of Painting' : 'Description of Jewelry'} *
                  </Label>
                  <Textarea 
                    className="mt-1 min-h-[120px]"
                    value={formData.description}
                    onChange={(e) => setFormData({...formData, description: e.target.value})}
                    placeholder={productType === 'painting' 
                      ? 'Describe your vision for the custom artwork...' 
                      : 'Describe your ideal jewelry piece (materials, colors, style)...'}
                    data-testid="custom-description"
                  />
                </div>

                <div className="bg-gray-50 p-4 rounded-sm space-y-2">
                  <p className="font-body text-sm text-gray-600">
                    <strong>Pricing:</strong> Small (4"x8") - $50 | Medium (12"x12") - $100 | Large (24"x36") - $150
                  </p>
                  {productType === 'painting' && (
                    <>
                      <p className="font-body text-sm text-gray-600">
                        <strong>Recreations:</strong> Interested in a sold piece? We can recreate any artwork in your preferred size.
                      </p>
                      <p className="font-body text-sm text-gray-600">
                        <strong>Framing:</strong> Professional framing is available with extra charges depending upon the design of frame.
                      </p>
                    </>
                  )}
                  {productType === 'jewelry' && (
                    <p className="font-body text-sm text-gray-600">
                      <strong>Custom Designs:</strong> Each jewelry piece is handmade with care. Share your vision and we'll create something unique for you.
                    </p>
                  )}
                </div>

                <Button 
                  type="submit" 
                  size="lg" 
                  className="w-full btn-gold font-body py-6"
                  disabled={loading}
                  data-testid="submit-custom-order"
                >
                  {loading ? 'Submitting...' : 'Submit Custom Order'}
                </Button>
              </form>
            </CardContent>
          </Card>
        </div>
      </section>
    </div>
  );
};

// Contact Page
const Contact = () => {
  const [showContactForm, setShowContactForm] = useState(false);
  const [contactForm, setContactForm] = useState({
    name: '',
    email: '',
    subject: '',
    message: ''
  });
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);

  const handleSendEmail = async (e) => {
    e.preventDefault();
    if (!contactForm.name || !contactForm.email || !contactForm.subject || !contactForm.message) {
      toast.error("Please fill in all fields");
      return;
    }

    setSending(true);
    try {
      await axios.post(`${API}/contact/send`, contactForm);
      toast.success("Message sent successfully!");
      setSent(true);
      setContactForm({ name: '', email: '', subject: '', message: '' });
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to send message");
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="pt-20 min-h-screen" data-testid="contact-page">
      <section className="py-16 px-6 md:px-12 text-center">
        <h1 className="font-heading text-5xl md:text-6xl mb-4">Get in Touch</h1>
        <p className="font-body text-gray-600 text-lg max-w-2xl mx-auto">
          Have questions? Want to discuss a commission? Reach out anytime.
        </p>
      </section>

      <section className="px-6 md:px-12 pb-12">
        <div className="max-w-4xl mx-auto">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <Card 
              className="text-center p-8 hover:shadow-lg transition-shadow cursor-pointer"
              onClick={() => setShowContactForm(true)}
              data-testid="email-card"
            >
              <CardContent className="pt-6">
                <div className="w-16 h-16 bg-black text-white rounded-full flex items-center justify-center mx-auto mb-4">
                  <Mail size={24} />
                </div>
                <h3 className="font-heading text-xl mb-2">Email</h3>
                <p className="font-body text-gray-600" data-testid="contact-email">
                  bajwahomegoods@gmail.com
                </p>
                <p className="font-body text-sm text-blue-600 mt-2">Click to send message</p>
              </CardContent>
            </Card>

            <Card className="text-center p-8 hover:shadow-lg transition-shadow">
              <CardContent className="pt-6">
                <div className="w-16 h-16 bg-black text-white rounded-full flex items-center justify-center mx-auto mb-4">
                  <Phone size={24} />
                </div>
                <h3 className="font-heading text-xl mb-2">Phone</h3>
                <a href="tel:724-272-8378" className="font-body text-gray-600 hover:text-black" data-testid="contact-phone">
                  724-272-8378
                </a>
              </CardContent>
            </Card>

            <Card className="text-center p-8 hover:shadow-lg transition-shadow">
              <CardContent className="pt-6">
                <div className="w-16 h-16 bg-black text-white rounded-full flex items-center justify-center mx-auto mb-4">
                  <MapPin size={24} />
                </div>
                <h3 className="font-heading text-xl mb-2">Address</h3>
                <p className="font-body text-gray-600" data-testid="contact-address">
                  Cranberry Township, PA 16066<br />
                  U.S.A.
                </p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Contact Form Section */}
      <section className="px-6 md:px-12 pb-24">
        <div className="max-w-2xl mx-auto">
          <Card className="border-0 shadow-lg">
            <CardHeader className="text-center">
              <CardTitle className="font-heading text-2xl">Send a Message</CardTitle>
              <p className="font-body text-gray-500 text-sm">Fill out the form below and we'll get back to you soon</p>
            </CardHeader>
            <CardContent>
              {sent ? (
                <div className="text-center py-8">
                  <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <Mail className="text-green-600" size={32} />
                  </div>
                  <h3 className="font-heading text-xl mb-2">Message Sent!</h3>
                  <p className="font-body text-gray-600 mb-4">Thank you for reaching out. We'll respond within 24-48 hours.</p>
                  <Button onClick={() => setSent(false)} variant="outline">Send Another Message</Button>
                </div>
              ) : (
                <form onSubmit={handleSendEmail} className="space-y-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <Label className="font-body text-sm">Your Name *</Label>
                      <Input
                        className="mt-1"
                        value={contactForm.name}
                        onChange={(e) => setContactForm({...contactForm, name: e.target.value})}
                        placeholder="John Doe"
                        data-testid="contact-form-name"
                      />
                    </div>
                    <div>
                      <Label className="font-body text-sm">Your Email *</Label>
                      <Input
                        type="email"
                        className="mt-1"
                        value={contactForm.email}
                        onChange={(e) => setContactForm({...contactForm, email: e.target.value})}
                        placeholder="john@example.com"
                        data-testid="contact-form-email"
                      />
                    </div>
                  </div>
                  <div>
                    <Label className="font-body text-sm">Subject *</Label>
                    <Input
                      className="mt-1"
                      value={contactForm.subject}
                      onChange={(e) => setContactForm({...contactForm, subject: e.target.value})}
                      placeholder="Question about a painting"
                      data-testid="contact-form-subject"
                    />
                  </div>
                  <div>
                    <Label className="font-body text-sm">Message *</Label>
                    <Textarea
                      className="mt-1 min-h-[150px]"
                      value={contactForm.message}
                      onChange={(e) => setContactForm({...contactForm, message: e.target.value})}
                      placeholder="Write your message here..."
                      data-testid="contact-form-message"
                    />
                  </div>
                  <Button
                    type="submit"
                    className="w-full btn-gold font-body py-6"
                    disabled={sending}
                    data-testid="contact-form-submit"
                  >
                    {sending ? 'Sending...' : 'Send Message'}
                  </Button>
                </form>
              )}
            </CardContent>
          </Card>
        </div>
      </section>
    </div>
  );
};

// User Login Page
const UserLogin = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const { loginUser, isUser } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (isUser) {
      navigate('/profile');
    }
  }, [isUser, navigate]);

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const response = await axios.post(`${API}/users/login`, { email, password });
      loginUser(response.data.access_token, response.data.user);
      toast.success("Welcome back!");
      navigate('/');
    } catch (error) {
      toast.error(error.response?.data?.detail || "Invalid credentials");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="pt-20 min-h-screen flex items-center justify-center px-6" data-testid="user-login-page">
      <Card className="w-full max-w-md">
        <CardHeader className="text-center">
          <CardTitle className="font-heading text-3xl">Welcome Back</CardTitle>
          <p className="font-body text-gray-500 text-sm mt-2">Login to your account</p>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleLogin} className="space-y-4">
            <div>
              <Label className="font-body text-sm">Email</Label>
              <Input 
                type="email"
                className="mt-1"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="your@email.com"
                data-testid="user-email"
              />
            </div>
            <div>
              <Label className="font-body text-sm">Password</Label>
              <Input 
                type="password"
                className="mt-1"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••"
                data-testid="user-password"
              />
            </div>
            <Button 
              type="submit" 
              className="w-full font-body"
              disabled={loading}
              data-testid="user-login-btn"
            >
              {loading ? 'Logging in...' : 'Login'}
            </Button>
          </form>
          <div className="mt-6 text-center">
            <p className="font-body text-sm text-gray-600">
              Don't have an account? <Link to="/register" className="text-black underline">Register</Link>
            </p>
            <p className="font-body text-xs text-gray-400 mt-4">
              <Link to="/admin/login" className="hover:text-black">Admin Login</Link>
            </p>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

// User Register Page
const UserRegister = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: '',
    confirmPassword: '',
    phone: '',
    address: '',
    other: ''
  });
  const [loading, setLoading] = useState(false);
  const { loginUser, isUser } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (isUser) {
      navigate('/profile');
    }
  }, [isUser, navigate]);

  const handleRegister = async (e) => {
    e.preventDefault();
    
    if (!formData.name || !formData.email || !formData.password) {
      toast.error("Please fill in name, email, and password");
      return;
    }
    
    if (formData.password.length < 6) {
      toast.error("Password must be at least 6 characters");
      return;
    }
    
    if (formData.password !== formData.confirmPassword) {
      toast.error("Passwords do not match");
      return;
    }

    setLoading(true);
    try {
      await axios.post(`${API}/users/register`, {
        name: formData.name,
        email: formData.email,
        password: formData.password,
        phone: formData.phone || null,
        address: formData.address || null,
        other: formData.other || null
      });
      
      // Auto-login after registration
      const loginRes = await axios.post(`${API}/users/login`, {
        email: formData.email,
        password: formData.password
      });
      loginUser(loginRes.data.access_token, loginRes.data.user);
      toast.success("Account created successfully!");
      navigate('/');
    } catch (error) {
      toast.error(error.response?.data?.detail || "Registration failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="pt-20 min-h-screen flex items-center justify-center px-6 py-12" data-testid="user-register-page">
      <Card className="w-full max-w-md">
        <CardHeader className="text-center">
          <CardTitle className="font-heading text-3xl">Create Account</CardTitle>
          <p className="font-body text-gray-500 text-sm mt-2">Join to save your info for faster checkout</p>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleRegister} className="space-y-4">
            <div>
              <Label className="font-body text-sm">Name *</Label>
              <Input 
                className="mt-1"
                value={formData.name}
                onChange={(e) => setFormData({...formData, name: e.target.value})}
                placeholder="Your full name"
                data-testid="register-name"
              />
            </div>
            <div>
              <Label className="font-body text-sm">Email *</Label>
              <Input 
                type="email"
                className="mt-1"
                value={formData.email}
                onChange={(e) => setFormData({...formData, email: e.target.value})}
                placeholder="your@email.com"
                data-testid="register-email"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="font-body text-sm">Password *</Label>
                <Input 
                  type="password"
                  className="mt-1"
                  value={formData.password}
                  onChange={(e) => setFormData({...formData, password: e.target.value})}
                  placeholder="Min 6 chars"
                  data-testid="register-password"
                />
              </div>
              <div>
                <Label className="font-body text-sm">Confirm *</Label>
                <Input 
                  type="password"
                  className="mt-1"
                  value={formData.confirmPassword}
                  onChange={(e) => setFormData({...formData, confirmPassword: e.target.value})}
                  placeholder="Confirm"
                  data-testid="register-confirm"
                />
              </div>
            </div>
            
            <div className="border-t pt-4 mt-4">
              <p className="font-body text-xs text-gray-500 mb-3">Optional - can be added later in profile</p>
              <div>
                <Label className="font-body text-sm">Phone</Label>
                <Input 
                  className="mt-1"
                  value={formData.phone}
                  onChange={(e) => setFormData({...formData, phone: e.target.value})}
                  placeholder="Your phone number"
                  data-testid="register-phone"
                />
              </div>
              <div className="mt-3">
                <Label className="font-body text-sm">Address</Label>
                <Input 
                  className="mt-1"
                  value={formData.address}
                  onChange={(e) => setFormData({...formData, address: e.target.value})}
                  placeholder="Shipping address"
                  data-testid="register-address"
                />
              </div>
              <div className="mt-3">
                <Label className="font-body text-sm">Other Info</Label>
                <Input 
                  className="mt-1"
                  value={formData.other}
                  onChange={(e) => setFormData({...formData, other: e.target.value})}
                  placeholder="Any other info"
                  data-testid="register-other"
                />
              </div>
            </div>

            <Button 
              type="submit" 
              className="w-full font-body btn-gold"
              disabled={loading}
              data-testid="register-btn"
            >
              {loading ? 'Creating Account...' : 'Create Account'}
            </Button>
          </form>
          <div className="mt-6 text-center">
            <p className="font-body text-sm text-gray-600">
              Already have an account? <Link to="/login" className="text-black underline">Login</Link>
            </p>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

// User Profile Page
const UserProfile = () => {
  const { user, token, isUser, updateUser, logout } = useAuth();
  const navigate = useNavigate();
  const [editing, setEditing] = useState(false);
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    address: '',
    other: ''
  });

  useEffect(() => {
    if (!isUser) {
      navigate('/login');
      return;
    }
    if (user) {
      setFormData({
        name: user.name || '',
        phone: user.phone || '',
        address: user.address || '',
        other: user.other || ''
      });
    }
  }, [isUser, user, navigate]);

  const handleSave = async () => {
    setLoading(true);
    try {
      const response = await axios.put(`${API}/users/profile`, formData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      updateUser(response.data);
      toast.success("Profile updated!");
      setEditing(false);
    } catch (error) {
      toast.error("Failed to update profile");
    } finally {
      setLoading(false);
    }
  };

  if (!user) return null;

  return (
    <div className="pt-20 min-h-screen px-6 py-12" data-testid="user-profile-page">
      <div className="max-w-2xl mx-auto">
        <h1 className="font-heading text-4xl mb-8">My Profile</h1>
        
        <Card>
          <CardContent className="p-6">
            {editing ? (
              <div className="space-y-4">
                <div>
                  <Label className="font-body text-sm">Name</Label>
                  <Input 
                    className="mt-1"
                    value={formData.name}
                    onChange={(e) => setFormData({...formData, name: e.target.value})}
                    data-testid="profile-name"
                  />
                </div>
                <div>
                  <Label className="font-body text-sm">Email</Label>
                  <Input 
                    className="mt-1"
                    value={user.email}
                    disabled
                    data-testid="profile-email"
                  />
                  <p className="font-body text-xs text-gray-400 mt-1">Email cannot be changed</p>
                </div>
                <div>
                  <Label className="font-body text-sm">Phone</Label>
                  <Input 
                    className="mt-1"
                    value={formData.phone}
                    onChange={(e) => setFormData({...formData, phone: e.target.value})}
                    placeholder="Your phone number"
                    data-testid="profile-phone"
                  />
                </div>
                <div>
                  <Label className="font-body text-sm">Address</Label>
                  <Input 
                    className="mt-1"
                    value={formData.address}
                    onChange={(e) => setFormData({...formData, address: e.target.value})}
                    placeholder="Shipping address"
                    data-testid="profile-address"
                  />
                </div>
                <div>
                  <Label className="font-body text-sm">Other Info</Label>
                  <Input 
                    className="mt-1"
                    value={formData.other}
                    onChange={(e) => setFormData({...formData, other: e.target.value})}
                    placeholder="Any other info"
                    data-testid="profile-other"
                  />
                </div>
                <div className="flex gap-3 pt-4">
                  <Button onClick={handleSave} disabled={loading} data-testid="save-profile-btn">
                    {loading ? 'Saving...' : 'Save Changes'}
                  </Button>
                  <Button variant="outline" onClick={() => setEditing(false)}>Cancel</Button>
                </div>
              </div>
            ) : (
              <div className="space-y-4">
                <div className="flex justify-between items-start">
                  <div>
                    <p className="font-body text-sm text-gray-500">Name</p>
                    <p className="font-body text-lg">{user.name}</p>
                  </div>
                  <Button variant="outline" size="sm" onClick={() => setEditing(true)} data-testid="edit-profile-btn">
                    <Edit size={14} className="mr-2" /> Edit
                  </Button>
                </div>
                <div>
                  <p className="font-body text-sm text-gray-500">Email</p>
                  <p className="font-body text-lg">{user.email}</p>
                </div>
                <div>
                  <p className="font-body text-sm text-gray-500">Phone</p>
                  <p className="font-body text-lg">{user.phone || <span className="text-gray-400 italic">Not provided</span>}</p>
                </div>
                <div>
                  <p className="font-body text-sm text-gray-500">Address</p>
                  <p className="font-body text-lg">{user.address || <span className="text-gray-400 italic">Not provided</span>}</p>
                </div>
                <div>
                  <p className="font-body text-sm text-gray-500">Other Info</p>
                  <p className="font-body text-lg">{user.other || <span className="text-gray-400 italic">Not provided</span>}</p>
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        <div className="mt-6 text-center">
          <Button variant="outline" className="text-red-600 border-red-600 hover:bg-red-50" onClick={logout}>
            <LogOut size={16} className="mr-2" /> Logout
          </Button>
        </div>
      </div>
    </div>
  );
};

// Admin Login Page
const AdminLogin = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const { loginAdmin, isAdmin } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (isAdmin) {
      navigate('/admin');
    }
  }, [isAdmin, navigate]);

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const response = await axios.post(`${API}/auth/login`, { email, password });
      loginAdmin(response.data.access_token);
      toast.success("Login successful");
      navigate('/admin');
    } catch (error) {
      toast.error(error.response?.data?.detail || "Invalid credentials");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="pt-20 min-h-screen flex items-center justify-center px-6" data-testid="admin-login-page">
      <Card className="w-full max-w-md">
        <CardHeader className="text-center">
          <CardTitle className="font-heading text-3xl">Admin Login</CardTitle>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleLogin} className="space-y-4">
            <div>
              <Label className="font-body text-sm">Email</Label>
              <Input 
                type="email"
                className="mt-1"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="admin@soniabajwaart.com"
                data-testid="admin-email"
              />
            </div>
            <div>
              <Label className="font-body text-sm">Password</Label>
              <Input 
                type="password"
                className="mt-1"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••"
                data-testid="admin-password"
              />
            </div>
            <Button 
              type="submit" 
              className="w-full font-body"
              disabled={loading}
              data-testid="admin-login-btn"
            >
              {loading ? 'Logging in...' : 'Login'}
            </Button>
          </form>
        </CardContent>
      </Card>
    </div>
  );
};

// Newsletter Tab Component
const NewsletterTab = ({ token, subscribers }) => {
  const [downloading, setDownloading] = useState(false);
  const [sendingReport, setSendingReport] = useState(false);

  const handleDownloadCSV = async () => {
    setDownloading(true);
    try {
      const response = await axios.get(`${API}/newsletter/export-csv`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      // Create and download file
      const blob = new Blob([response.data.csv_content], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = response.data.filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
      
      toast.success(`Downloaded ${response.data.count} subscribers`);
    } catch (error) {
      toast.error("Failed to download CSV");
    } finally {
      setDownloading(false);
    }
  };

  const handleSendDailyReport = async () => {
    setSendingReport(true);
    try {
      const response = await axios.post(`${API}/newsletter/send-daily-report`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(response.data.message);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to send report");
    } finally {
      setSendingReport(false);
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-6">
        <p className="font-body text-gray-600">{subscribers.length} subscribers</p>
        <div className="flex gap-3">
          <Button 
            variant="outline" 
            onClick={handleDownloadCSV}
            disabled={downloading || subscribers.length === 0}
            data-testid="download-csv-btn"
          >
            <Download size={16} className="mr-2" />
            {downloading ? 'Downloading...' : 'Download CSV'}
          </Button>
          <Button 
            onClick={handleSendDailyReport}
            disabled={sendingReport || subscribers.length === 0}
            data-testid="send-report-btn"
          >
            <Send size={16} className="mr-2" />
            {sendingReport ? 'Sending...' : 'Send Report Now'}
          </Button>
        </div>
      </div>
      
      <Card className="p-4 bg-blue-50 border-blue-200">
        <p className="font-body text-sm text-blue-800">
          <strong>Automated Daily Reports:</strong> A CSV file with all subscribers is automatically emailed to bajwahomegoods@gmail.com every 24 hours.
        </p>
      </Card>

      {subscribers.length === 0 ? (
        <p className="font-body text-gray-500 text-center py-12">No newsletter subscribers yet</p>
      ) : (
        <Card className="overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 border-b">
                <tr>
                  <th className="text-left font-body text-sm font-semibold p-4">Email</th>
                  <th className="text-left font-body text-sm font-semibold p-4">Name</th>
                  <th className="text-left font-body text-sm font-semibold p-4">Subscribed</th>
                </tr>
              </thead>
              <tbody>
                {subscribers.map((sub) => (
                  <tr key={sub.id} className="border-b hover:bg-gray-50">
                    <td className="p-4 font-body text-sm">{sub.email}</td>
                    <td className="p-4 font-body text-sm text-gray-500">{sub.name || '-'}</td>
                    <td className="p-4 font-body text-sm text-gray-500">
                      {new Date(sub.subscribed_at).toLocaleDateString()}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      )}
    </div>
  );
};

// Add Painting Form Component with File Upload
const AddPaintingForm = ({ token, onSuccess, onCancel }) => {
  const [imageSource, setImageSource] = useState('url'); // 'url' or 'upload'
  const [uploading, setUploading] = useState(false);
  const [previewUrl, setPreviewUrl] = useState('');
  const [productType, setProductType] = useState('painting');
  const [newPainting, setNewPainting] = useState({
    title: '',
    description: '',
    product_type: 'painting',
    category: 'Oil',
    image_url: '',
    price: 100,
    is_available: true
  });

  const paintingCategories = ['Oil', 'Acrylic', 'Watercolor', 'Mixed Media', 'Other'];
  const jewelryCategories = ['Necklaces', 'Earrings', 'Bracelets', 'Rings', 'Other'];
  const categories = productType === 'painting' ? paintingCategories : jewelryCategories;

  const handleProductTypeChange = (type) => {
    setProductType(type);
    const defaultCat = type === 'painting' ? 'Oil' : 'Necklaces';
    setNewPainting({ ...newPainting, product_type: type, category: defaultCat });
  };

  const handleFileChange = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Show preview
    const reader = new FileReader();
    reader.onload = (e) => setPreviewUrl(e.target.result);
    reader.readAsDataURL(file);

    // Upload file
    setUploading(true);
    const formData = new FormData();
    formData.append('file', file);

    try {
      const response = await axios.post(`${API}/upload`, formData, {
        headers: { 
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        }
      });
      // Cloudinary returns the full URL directly
      setNewPainting({...newPainting, image_url: response.data.url});
      toast.success("Image uploaded successfully!");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to upload image");
      setPreviewUrl('');
    } finally {
      setUploading(false);
    }
  };

  const handleSubmit = async () => {
    if (!newPainting.title || !newPainting.image_url) {
      toast.error("Please fill in title and add an image");
      return;
    }

    try {
      await axios.post(`${API}/paintings`, newPainting, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Painting added successfully!");
      onSuccess();
    } catch (error) {
      toast.error("Failed to add painting");
    }
  };

  return (
    <div className="space-y-4 py-4">
      {/* Product Type Selector */}
      <div>
        <Label className="font-body text-sm">Product Type *</Label>
        <div className="flex gap-2 mt-1">
          <Button
            type="button"
            variant={productType === 'painting' ? 'default' : 'outline'}
            size="sm"
            onClick={() => handleProductTypeChange('painting')}
            data-testid="new-product-type-painting"
          >
            <Palette size={16} className="mr-2" /> Painting
          </Button>
          <Button
            type="button"
            variant={productType === 'jewelry' ? 'default' : 'outline'}
            size="sm"
            onClick={() => handleProductTypeChange('jewelry')}
            data-testid="new-product-type-jewelry"
          >
            Jewelry
          </Button>
        </div>
      </div>

      <div>
        <Label className="font-body text-sm">Title *</Label>
        <Input 
          className="mt-1"
          value={newPainting.title}
          onChange={(e) => setNewPainting({...newPainting, title: e.target.value})}
          placeholder={productType === 'painting' ? 'Enter painting title' : 'Enter jewelry name'}
          data-testid="new-painting-title"
        />
      </div>
      
      <div>
        <Label className="font-body text-sm">Description</Label>
        <Textarea 
          className="mt-1"
          value={newPainting.description}
          onChange={(e) => setNewPainting({...newPainting, description: e.target.value})}
          placeholder={productType === 'painting' ? 'Describe the painting...' : 'Describe the jewelry piece...'}
          data-testid="new-painting-description"
        />
      </div>
      
      <div>
        <Label className="font-body text-sm">Category</Label>
        <Select value={newPainting.category} onValueChange={(value) => setNewPainting({...newPainting, category: value})}>
          <SelectTrigger className="mt-1" data-testid="new-painting-category">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {categories.map((cat) => (
              <SelectItem key={cat} value={cat}>{cat}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Image Source Toggle */}
      <div>
        <Label className="font-body text-sm mb-2 block">Image *</Label>
        <div className="flex gap-2 mb-3">
          <Button
            type="button"
            variant={imageSource === 'upload' ? 'default' : 'outline'}
            size="sm"
            onClick={() => setImageSource('upload')}
            data-testid="image-source-upload"
          >
            <Upload size={16} className="mr-2" /> Upload from Device
          </Button>
          <Button
            type="button"
            variant={imageSource === 'url' ? 'default' : 'outline'}
            size="sm"
            onClick={() => setImageSource('url')}
            data-testid="image-source-url"
          >
            <Image size={16} className="mr-2" /> Use URL
          </Button>
        </div>

        {imageSource === 'upload' ? (
          <div className="space-y-3">
            <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center hover:border-gray-400 transition-colors">
              <input
                type="file"
                accept="image/*"
                onChange={handleFileChange}
                className="hidden"
                id="painting-upload"
                data-testid="painting-file-input"
              />
              <label htmlFor="painting-upload" className="cursor-pointer">
                {uploading ? (
                  <div className="flex flex-col items-center">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-black mb-2"></div>
                    <p className="font-body text-sm text-gray-600">Uploading...</p>
                  </div>
                ) : previewUrl ? (
                  <div className="space-y-2">
                    <img src={previewUrl} alt="Preview" className="max-h-40 mx-auto rounded" />
                    <p className="font-body text-sm text-green-600">Image uploaded! Click to change.</p>
                  </div>
                ) : (
                  <div className="flex flex-col items-center">
                    <Upload size={32} className="text-gray-400 mb-2" />
                    <p className="font-body text-sm text-gray-600">Click to upload image</p>
                    <p className="font-body text-xs text-gray-400 mt-1">JPG, PNG, GIF, WEBP supported</p>
                  </div>
                )}
              </label>
            </div>
          </div>
        ) : (
          <Input 
            className="mt-1"
            value={newPainting.image_url}
            onChange={(e) => setNewPainting({...newPainting, image_url: e.target.value})}
            placeholder="https://example.com/image.jpg"
            data-testid="new-painting-image-url"
          />
        )}
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div>
          <Label className="font-body text-sm">Price ($)</Label>
          <Input 
            type="number"
            className="mt-1"
            value={newPainting.price}
            onChange={(e) => setNewPainting({...newPainting, price: parseFloat(e.target.value) || 0})}
            data-testid="new-painting-price"
          />
        </div>
        <div className="flex items-end pb-2">
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              checked={!newPainting.is_available}
              onChange={(e) => setNewPainting({...newPainting, is_available: !e.target.checked})}
              data-testid="new-painting-sold"
            />
            <span className="font-body text-sm">Mark as Sold</span>
          </label>
        </div>
      </div>

      <DialogFooter className="pt-4">
        <Button variant="outline" onClick={onCancel}>Cancel</Button>
        <Button onClick={handleSubmit} disabled={uploading} data-testid="save-new-painting">
          Save Painting
        </Button>
      </DialogFooter>
    </div>
  );
};

// Change Password Form Component
const ChangePasswordForm = ({ token }) => {
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);

  const handleChangePassword = async (e) => {
    e.preventDefault();
    
    if (!currentPassword || !newPassword || !confirmPassword) {
      toast.error("Please fill in all fields");
      return;
    }
    
    if (newPassword.length < 6) {
      toast.error("New password must be at least 6 characters");
      return;
    }
    
    if (newPassword !== confirmPassword) {
      toast.error("New passwords do not match");
      return;
    }

    setLoading(true);
    try {
      await axios.post(`${API}/auth/change-password`, {
        current_password: currentPassword,
        new_password: newPassword
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Password changed successfully!");
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to change password");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="max-w-md">
      <Card>
        <CardHeader>
          <CardTitle className="font-heading text-2xl flex items-center gap-2">
            <Lock size={24} /> Change Password
          </CardTitle>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleChangePassword} className="space-y-4">
            <div>
              <Label className="font-body text-sm">Current Password</Label>
              <div className="relative mt-1">
                <Input
                  type={showCurrentPassword ? "text" : "password"}
                  value={currentPassword}
                  onChange={(e) => setCurrentPassword(e.target.value)}
                  placeholder="Enter current password"
                  data-testid="current-password"
                />
                <button
                  type="button"
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-black"
                  onClick={() => setShowCurrentPassword(!showCurrentPassword)}
                >
                  {showCurrentPassword ? 'Hide' : 'Show'}
                </button>
              </div>
            </div>
            
            <div>
              <Label className="font-body text-sm">New Password</Label>
              <div className="relative mt-1">
                <Input
                  type={showNewPassword ? "text" : "password"}
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  placeholder="Enter new password (min 6 characters)"
                  data-testid="new-password"
                />
                <button
                  type="button"
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-black"
                  onClick={() => setShowNewPassword(!showNewPassword)}
                >
                  {showNewPassword ? 'Hide' : 'Show'}
                </button>
              </div>
            </div>
            
            <div>
              <Label className="font-body text-sm">Confirm New Password</Label>
              <Input
                type="password"
                className="mt-1"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                placeholder="Confirm new password"
                data-testid="confirm-password"
              />
            </div>

            <Button
              type="submit"
              className="w-full font-body"
              disabled={loading}
              data-testid="change-password-btn"
            >
              {loading ? 'Changing...' : 'Change Password'}
            </Button>
          </form>
        </CardContent>
      </Card>
    </div>
  );
};

// Admin Dashboard
const AdminDashboard = () => {
  const { isAdmin, token } = useAuth();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('paintings');
  const [paintings, setPaintings] = useState([]);
  const [orders, setOrders] = useState([]);
  const [purchases, setPurchases] = useState([]);
  const [subscribers, setSubscribers] = useState([]);
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [selectedPainting, setSelectedPainting] = useState(null);

  useEffect(() => {
    if (!isAdmin) {
      navigate('/admin/login');
      return;
    }
    fetchData();
  }, [isAdmin, navigate]);

  const fetchData = async () => {
    try {
      const headers = { Authorization: `Bearer ${token}` };
      const [paintingsRes, ordersRes, purchasesRes, subscribersRes] = await Promise.all([
        axios.get(`${API}/paintings`),
        axios.get(`${API}/orders/custom`, { headers }),
        axios.get(`${API}/purchases`, { headers }),
        axios.get(`${API}/newsletter/subscribers`, { headers })
      ]);
      setPaintings(paintingsRes.data);
      setOrders(ordersRes.data);
      setPurchases(purchasesRes.data);
      setSubscribers(subscribersRes.data);
    } catch (error) {
      toast.error("Failed to load data");
    }
  };

  const handleUpdatePainting = async () => {
    try {
      await axios.put(`${API}/paintings/${selectedPainting.id}`, selectedPainting, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Painting updated successfully");
      setShowEditDialog(false);
      setSelectedPainting(null);
      fetchData();
    } catch (error) {
      toast.error("Failed to update painting");
    }
  };

  const handleDeletePainting = async (id) => {
    if (!window.confirm("Are you sure you want to delete this painting?")) return;
    try {
      await axios.delete(`${API}/paintings/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Painting deleted");
      fetchData();
    } catch (error) {
      toast.error("Failed to delete painting");
    }
  };

  const handleUpdateOrderStatus = async (orderId, status) => {
    try {
      await axios.put(`${API}/orders/custom/${orderId}/status?status=${status}`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Order status updated");
      fetchData();
    } catch (error) {
      toast.error("Failed to update order status");
    }
  };

  if (!isAdmin) return null;

  return (
    <div className="pt-20 min-h-screen bg-gray-50" data-testid="admin-dashboard">
      <div className="max-w-7xl mx-auto px-6 md:px-12 py-8">
        <h1 className="font-heading text-4xl mb-8">Admin Dashboard</h1>

        {/* Tabs */}
        <div className="flex gap-4 mb-8 border-b border-gray-200 overflow-x-auto hide-scrollbar">
          {[
            { id: 'paintings', label: 'Paintings', icon: Image },
            { id: 'orders', label: 'Custom Orders', icon: Package },
            { id: 'purchases', label: 'Purchases', icon: ShoppingCart },
            { id: 'newsletter', label: 'Newsletter', icon: Mail },
            { id: 'settings', label: 'Settings', icon: Lock }
          ].map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex items-center gap-2 px-4 py-3 font-body text-sm border-b-2 transition-colors ${
                activeTab === tab.id 
                  ? 'border-black text-black' 
                  : 'border-transparent text-gray-500 hover:text-black'
              }`}
              data-testid={`admin-tab-${tab.id}`}
            >
              <tab.icon size={18} />
              {tab.label}
            </button>
          ))}
        </div>

        {/* Paintings Tab */}
        {activeTab === 'paintings' && (
          <div>
            <div className="flex justify-between items-center mb-6">
              <p className="font-body text-gray-600">{paintings.length} items</p>
              <Button onClick={() => setShowAddDialog(true)} data-testid="add-painting-btn">
                <Plus size={18} className="mr-2" /> Add Item
              </Button>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {paintings.map((painting) => (
                <Card key={painting.id} className="overflow-hidden">
                  <div className="aspect-[4/3] bg-gray-100">
                    <img src={painting.image_url} alt={painting.title} className="w-full h-full object-cover" />
                  </div>
                  <CardContent className="p-4">
                    <div className="flex justify-between items-start mb-2">
                      <div>
                        <h3 className="font-heading text-lg">{painting.title}</h3>
                        <p className="font-body text-sm text-gray-500">{painting.category}</p>
                      </div>
                      <div className="flex flex-col items-end gap-1">
                        <Badge variant={painting.is_available ? "default" : "secondary"}>
                          {painting.is_available ? 'Available' : 'Sold'}
                        </Badge>
                        <Badge variant="outline" className="text-xs capitalize">
                          {painting.product_type || 'painting'}
                        </Badge>
                      </div>
                    </div>
                    <p className="font-accent text-lg font-semibold mb-4">${painting.price.toFixed(2)}</p>
                    <div className="flex gap-2">
                      <Button 
                        variant="outline" 
                        size="sm" 
                        onClick={() => { setSelectedPainting(painting); setShowEditDialog(true); }}
                        data-testid={`edit-painting-${painting.id}`}
                      >
                        <Edit size={14} className="mr-1" /> Edit
                      </Button>
                      <Button 
                        variant="destructive" 
                        size="sm" 
                        onClick={() => handleDeletePainting(painting.id)}
                        data-testid={`delete-painting-${painting.id}`}
                      >
                        <Trash2 size={14} className="mr-1" /> Delete
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        )}

        {/* Custom Orders Tab */}
        {activeTab === 'orders' && (
          <div className="space-y-4">
            {orders.length === 0 ? (
              <p className="font-body text-gray-500 text-center py-12">No custom orders yet</p>
            ) : (
              orders.map((order) => (
                <Card key={order.id} className="p-6">
                  <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                    <div>
                      <h3 className="font-heading text-xl mb-1">{order.customer_name}</h3>
                      <p className="font-body text-sm text-gray-500">{order.customer_email}</p>
                      <p className="font-body text-sm mt-2">
                        <strong>Type:</strong> {order.product_type === 'jewelry' ? 'Jewelry' : 'Painting'} | 
                        {order.product_type === 'jewelry' 
                          ? <><strong> Jewelry:</strong> {order.jewelry_type}</>
                          : <><strong> Medium:</strong> {order.color_type}</>
                        } | <strong>Size:</strong> {order.size} | <strong>Price:</strong> ${order.price.toFixed(2)}
                      </p>
                      <p className="font-body text-sm text-gray-600 mt-2">{order.description}</p>
                    </div>
                    <div className="flex items-center gap-4">
                      <Select value={order.status} onValueChange={(value) => handleUpdateOrderStatus(order.id, value)}>
                        <SelectTrigger className="w-40" data-testid={`order-status-${order.id}`}>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="pending">Pending</SelectItem>
                          <SelectItem value="accepted">Accepted</SelectItem>
                          <SelectItem value="in_progress">In Progress</SelectItem>
                          <SelectItem value="completed">Completed</SelectItem>
                          <SelectItem value="cancelled">Cancelled</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                </Card>
              ))
            )}
          </div>
        )}

        {/* Purchases Tab */}
        {activeTab === 'purchases' && (
          <div className="space-y-4">
            {purchases.length === 0 ? (
              <p className="font-body text-gray-500 text-center py-12">No purchases yet</p>
            ) : (
              purchases.map((purchase) => (
                <Card key={purchase.id} className="p-6">
                  <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                    <div>
                      <h3 className="font-heading text-xl mb-1">{purchase.customer_name}</h3>
                      <p className="font-body text-sm text-gray-500">{purchase.customer_email}</p>
                      <p className="font-body text-sm mt-2">
                        <strong>Amount:</strong> ${purchase.amount.toFixed(2)} | <strong>Painting ID:</strong> {purchase.painting_id.slice(0, 8)}...
                      </p>
                    </div>
                    <Badge className={`status-${purchase.status}`}>
                      {purchase.status}
                    </Badge>
                  </div>
                </Card>
              ))
            )}
          </div>
        )}

        {/* Newsletter Tab */}
        {activeTab === 'newsletter' && (
          <NewsletterTab token={token} subscribers={subscribers} />
        )}

        {/* Settings Tab */}
        {activeTab === 'settings' && (
          <ChangePasswordForm token={token} />
        )}
      </div>

      {/* Add Painting Dialog */}
      <Dialog open={showAddDialog} onOpenChange={setShowAddDialog}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-heading text-2xl">Add New Item</DialogTitle>
          </DialogHeader>
          <AddPaintingForm 
            token={token} 
            onSuccess={() => { setShowAddDialog(false); fetchData(); }}
            onCancel={() => setShowAddDialog(false)}
          />
        </DialogContent>
      </Dialog>

      {/* Edit Painting Dialog */}
      <Dialog open={showEditDialog} onOpenChange={setShowEditDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-heading text-2xl">Edit Item</DialogTitle>
          </DialogHeader>
          {selectedPainting && (
            <div className="space-y-4 py-4">
              {/* Product Type Selector */}
              <div>
                <Label className="font-body text-sm">Product Type</Label>
                <div className="flex gap-2 mt-1">
                  <Button
                    type="button"
                    variant={(selectedPainting.product_type || 'painting') === 'painting' ? 'default' : 'outline'}
                    size="sm"
                    onClick={() => setSelectedPainting({...selectedPainting, product_type: 'painting', category: 'Oil'})}
                    data-testid="edit-product-type-painting"
                  >
                    <Palette size={16} className="mr-2" /> Painting
                  </Button>
                  <Button
                    type="button"
                    variant={(selectedPainting.product_type || 'painting') === 'jewelry' ? 'default' : 'outline'}
                    size="sm"
                    onClick={() => setSelectedPainting({...selectedPainting, product_type: 'jewelry', category: 'Necklaces'})}
                    data-testid="edit-product-type-jewelry"
                  >
                    Jewelry
                  </Button>
                </div>
              </div>
              <div>
                <Label className="font-body text-sm">Title</Label>
                <Input 
                  className="mt-1"
                  value={selectedPainting.title}
                  onChange={(e) => setSelectedPainting({...selectedPainting, title: e.target.value})}
                  data-testid="edit-painting-title"
                />
              </div>
              <div>
                <Label className="font-body text-sm">Description</Label>
                <Textarea 
                  className="mt-1"
                  value={selectedPainting.description}
                  onChange={(e) => setSelectedPainting({...selectedPainting, description: e.target.value})}
                  data-testid="edit-painting-description"
                />
              </div>
              <div>
                <Label className="font-body text-sm">Category</Label>
                <Select value={selectedPainting.category} onValueChange={(value) => setSelectedPainting({...selectedPainting, category: value})}>
                  <SelectTrigger className="mt-1" data-testid="edit-painting-category">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {((selectedPainting.product_type || 'painting') === 'painting' 
                      ? ['Oil', 'Acrylic', 'Watercolor', 'Mixed Media', 'Other'] 
                      : ['Necklaces', 'Earrings', 'Bracelets', 'Rings', 'Other']
                    ).map((cat) => (
                      <SelectItem key={cat} value={cat}>{cat}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="font-body text-sm">Image URL</Label>
                <Input 
                  className="mt-1"
                  value={selectedPainting.image_url}
                  onChange={(e) => setSelectedPainting({...selectedPainting, image_url: e.target.value})}
                  data-testid="edit-painting-image"
                />
              </div>
              <div>
                <Label className="font-body text-sm">Price ($)</Label>
                <Input 
                  type="number"
                  className="mt-1"
                  value={selectedPainting.price}
                  onChange={(e) => setSelectedPainting({...selectedPainting, price: parseFloat(e.target.value) || 0})}
                  data-testid="edit-painting-price"
                />
              </div>
              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={selectedPainting.is_available}
                  onChange={(e) => setSelectedPainting({...selectedPainting, is_available: e.target.checked})}
                  data-testid="edit-painting-available"
                />
                <Label className="font-body text-sm">Available for sale</Label>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowEditDialog(false)}>Cancel</Button>
            <Button onClick={handleUpdatePainting} data-testid="save-edit-painting">Save Changes</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

// Payment Success Page
const PaymentSuccess = () => {
  const [searchParams] = useSearchParams();
  const sessionId = searchParams.get('session_id');
  const [status, setStatus] = useState('checking');
  const [paymentData, setPaymentData] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    if (sessionId) {
      checkPaymentStatus();
    }
  }, [sessionId]);

  const checkPaymentStatus = async () => {
    try {
      const response = await axios.get(`${API}/payments/status/${sessionId}`);
      setPaymentData(response.data);
      if (response.data.payment_status === 'paid') {
        setStatus('paid');
      } else {
        // Poll again after 2 seconds
        setTimeout(checkPaymentStatus, 2000);
      }
    } catch {
      setStatus('error');
    }
  };

  return (
    <div className="pt-20 min-h-screen flex items-center justify-center px-6" data-testid="payment-success-page">
      <div className="text-center max-w-lg">
        {status === 'checking' && (
          <>
            <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-black mx-auto mb-6"></div>
            <h1 className="font-heading text-3xl mb-4">Confirming Payment...</h1>
            <p className="font-body text-gray-600">Please wait while we verify your payment.</p>
          </>
        )}
        {status === 'paid' && (
          <>
            <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <CheckCircle className="text-green-600" size={40} />
            </div>
            <h1 className="font-heading text-4xl mb-4">Payment Successful!</h1>
            <p className="font-body text-gray-600 mb-2">
              Thank you for your purchase. Your order has been confirmed.
            </p>
            {paymentData && (
              <p className="font-accent text-xl font-semibold mb-6">
                Amount: ${paymentData.amount?.toFixed(2)}
              </p>
            )}
            <p className="font-body text-sm text-gray-500 mb-8">
              Sonia will be in touch regarding shipping details. You'll receive a confirmation email shortly.
            </p>
            <Button onClick={() => navigate('/gallery')} className="font-body" data-testid="back-to-gallery-btn">
              Continue Browsing
            </Button>
          </>
        )}
        {status === 'error' && (
          <>
            <div className="w-20 h-20 bg-yellow-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <CheckCircle className="text-yellow-600" size={40} />
            </div>
            <h1 className="font-heading text-3xl mb-4">Payment Received</h1>
            <p className="font-body text-gray-600 mb-8">
              Your payment is being processed. If you have any questions, please contact us.
            </p>
            <Button onClick={() => navigate('/gallery')} className="font-body">
              Continue Browsing
            </Button>
          </>
        )}
      </div>
    </div>
  );
};

// Payment Cancel Page
const PaymentCancel = () => {
  const navigate = useNavigate();

  return (
    <div className="pt-20 min-h-screen flex items-center justify-center px-6" data-testid="payment-cancel-page">
      <div className="text-center max-w-lg">
        <div className="w-20 h-20 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-6">
          <XCircle className="text-gray-500" size={40} />
        </div>
        <h1 className="font-heading text-4xl mb-4">Payment Cancelled</h1>
        <p className="font-body text-gray-600 mb-8">
          No worries! Your payment was not processed. The item is still available if you'd like to try again.
        </p>
        <div className="flex gap-4 justify-center">
          <Button onClick={() => navigate('/gallery')} className="font-body" data-testid="cancel-back-gallery">
            Back to Gallery
          </Button>
          <Button variant="outline" onClick={() => navigate('/contact')} className="font-body">
            Contact Us
          </Button>
        </div>
      </div>
    </div>
  );
};

function App() {
  useEffect(() => {
    // Seed data on first load
    axios.post(`${API}/seed`).catch(() => {});
  }, []);

  return (
    <AuthProvider>
      <div className="App">
        <BrowserRouter>
          <Navigation />
          <Toaster position="top-right" richColors />
          <Routes>
            <Route path="/" element={<Home />} />
            <Route path="/gallery" element={<Gallery />} />
            <Route path="/painting/:id" element={<PaintingDetail />} />
            <Route path="/custom-order" element={<CustomOrder />} />
            <Route path="/contact" element={<Contact />} />
            <Route path="/login" element={<UserLogin />} />
            <Route path="/register" element={<UserRegister />} />
            <Route path="/profile" element={<UserProfile />} />
            <Route path="/admin/login" element={<AdminLogin />} />
            <Route path="/admin" element={<AdminDashboard />} />
            <Route path="/payment/success" element={<PaymentSuccess />} />
            <Route path="/payment/cancel" element={<PaymentCancel />} />
          </Routes>
          <Footer />
        </BrowserRouter>
      </div>
    </AuthProvider>
  );
}

export default App;
